import create from 'zustand';
import { FilterType, TagType } from '@/components/shared/Correlations/types';
import { ChildrenMap } from '@/components/shared/Standards/standardsTreeUtils';
import { StandardNode } from '@/components/shared/Standards/types';
import { applyCheck, ParentMap } from './standardsFilterUtils';

interface ICorrelationsStoreValues {
  filteredTags: TagType[];
  filters: FilterType;
  isLoading: boolean;
  tags: TagType[];
  unitLessonMap: { [key: number]: number };
  setFilteredTags: (filteredTags: TagType[]) => void;
  setFilters: (filters: FilterType) => void;
  setTags: (tags: TagType[]) => void;
  setUnitLessonMap: (unitLessonMap: { [key: number]: number }) => void;
  // Flag-on unit/lesson selection, sent to the server as lesson_ids. `filters`/`filteredTags` above are
  // the flag-off client-side path.
  lessonsCheckedIds: Set<number>;
  checkLessons: (ids: number[], checked: boolean) => void;
  clearLessons: () => void;
  // "Show X more" open/closed per filter section; in the store so it survives the <Filters> remount.
  categoryExpanded: { standards: boolean; unit: boolean };
  setCategoryExpanded: (section: 'standards' | 'unit', value: boolean) => void;
  // Standards filter tree state lives here (not in the tree components) so it survives the
  // <Filters> remount on every page change. standardsNodes mirrors what react-query has loaded so
  // the cascade/indeterminate maps can be derived reactively in one place.
  standardsAutoExpandedIds: Set<number>;
  standardsCheckedIds: Set<number>;
  standardsExpandedIds: Set<number>;
  standardsInitKey: string | null;
  standardsNodes: Record<number, StandardNode>;
  standardsQuery: string;
  autoExpandStandards: (ids: number[], initKey: string) => void;
  checkStandard: (id: number, checked: boolean, maps: { parentMap: ParentMap, childrenMap: ChildrenMap }) => void;
  clearStandards: () => void;
  mergeStandardsNodes: (nodes: StandardNode[]) => void;
  setStandardsQuery: (standardsQuery: string) => void;
  toggleStandardExpanded: (id: number) => void;
}

const useCorrelationsStore = create<ICorrelationsStoreValues>((set, get) => ({
  // Default "roots expanded one level": each root is auto-expanded once per tree (initKey), so
  // roots loaded by a later "See more" page get the same treatment without re-opening roots the
  // teacher has collapsed.
  autoExpandStandards: (ids, initKey) => {
    const fresh = get().standardsInitKey !== initKey;
    const autoExpanded = fresh ? new Set<number>() : new Set(get().standardsAutoExpandedIds);
    const pending = ids.filter(id => !autoExpanded.has(id));
    if (!fresh && pending.length === 0) return;

    const expanded = fresh ? new Set<number>() : new Set(get().standardsExpandedIds);
    pending.forEach((id) => { expanded.add(id); autoExpanded.add(id); });

    set({
      standardsAutoExpandedIds: autoExpanded,
      standardsCheckedIds: fresh ? new Set() : get().standardsCheckedIds,
      standardsExpandedIds: expanded,
      standardsInitKey: initKey,
    });
  },
  checkLessons: (ids, checked) => {
    const next = new Set(get().lessonsCheckedIds);
    ids.forEach(id => (checked ? next.add(id) : next.delete(id)));
    set({ lessonsCheckedIds: next });
  },
  checkStandard: (id, checked, { parentMap, childrenMap }) => {
    set({ standardsCheckedIds: applyCheck(get().standardsCheckedIds, id, checked, parentMap, childrenMap) });
  },
  clearLessons: () => set({
    lessonsCheckedIds: new Set(),
    categoryExpanded: { ...get().categoryExpanded, unit: false },
  }),
  clearStandards: () => set({
    standardsCheckedIds: new Set(),
    standardsQuery: '',
    categoryExpanded: { ...get().categoryExpanded, standards: false },
  }),
  filteredTags: [],
  filters: {},
  isLoading: false,
  lessonsCheckedIds: new Set(),
  mergeStandardsNodes: (nodes) => {
    const current = get().standardsNodes;
    if (nodes.every(node => current[node.id])) return;

    set({ standardsNodes: { ...current, ...Object.fromEntries(nodes.map(node => [node.id, node])) } });
  },
  setFilteredTags: filteredTags => set({ filteredTags }),
  setFilters: (filters) => {
    const { tags, setFilteredTags, unitLessonMap } = get();

    const lessonIds = Object.keys(filters)
      .map(key => filters[key])
      .flatMap(filter => (filter.filter(f => f.checked).map(f => f.value)));

    set({ filters });

    if (lessonIds.length === 0) {
      get().setFilteredTags([]);
    } else {
      const filteredTags = tags
        .filter(tag => tag.lessons.some(lesson => lessonIds.includes(lesson.id)))
        .map((tag) => {
          const unitIds = lessonIds.map(lessonId => unitLessonMap[lessonId]);

          return ({
            ...tag,
            lessons: tag.lessons.filter(lesson => lessonIds.includes(lesson.id)),
            units: tag.units?.filter(unit => unitIds.includes(unit.id)),
          });
        });

      setFilteredTags(filteredTags);
    }
  },
  // A query change re-measures the collapsed section, so the "Show X more" count matches the results.
  setStandardsQuery: (standardsQuery) => {
    const { categoryExpanded } = get();

    set({
      standardsQuery,
      ...(categoryExpanded.standards ? { categoryExpanded: { ...categoryExpanded, standards: false } } : {}),
    });
  },
  setTags: tags => set({ tags }),
  setCategoryExpanded: (section, value) => {
    set({ categoryExpanded: { ...get().categoryExpanded, [section]: value } });
  },
  setUnitLessonMap: unitLessonMap => set({ unitLessonMap }),
  standardsAutoExpandedIds: new Set(),
  standardsCheckedIds: new Set(),
  standardsExpandedIds: new Set(),
  standardsInitKey: null,
  standardsNodes: {},
  standardsQuery: '',
  tags: [],
  toggleStandardExpanded: (id) => {
    const next = new Set(get().standardsExpandedIds);

    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }

    set({ standardsExpandedIds: next });
  },
  categoryExpanded: { standards: false, unit: false },
  unitLessonMap: {},
}));

export default useCorrelationsStore;
